/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Modal,
	Form,
	Input,
	Select,
	DatePicker,
	Button,
	Checkbox,
	Radio,
	Alert,
	message,
} from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Box } from 'atoms';
import { searchVehiclesV2, searchVehicleTypesV2 } from 'store/vehicleSliceV2';
import { searchDriversV2 } from 'store/driverSliceV2';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { replaceTripVehicleV2 } from 'store/tripSliceV2';
import { BILLING_TYPE_FIXED_PER_DAY } from 'componentsv2/forms/VendorContractFormV2.constants';

const { Option } = Select;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 8px;
	}

	.ant-modal-header {
		border-bottom: none;
		padding: 24px 24px 0;
	}

	.ant-modal-title {
		font-size: 20px;
		font-weight: 600;
		color: #1f2937;
	}

	.ant-modal-body {
		padding: 24px;
		max-height: 70vh;
		overflow-y: auto;
	}

	.ant-modal-footer {
		border-top: none;
		padding: 0 24px 24px;
	}
`;

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #374151;
		font-size: 14px;
	}

	.ant-input,
	.ant-select-selector,
	.ant-picker {
		height: 40px;
		border-radius: 6px;
		border: 1px solid #d1d5db;
		background-color: #fff;
	}

	input[type='number']::-webkit-outer-spin-button,
	input[type='number']::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	input[type='number'] {
		-moz-appearance: textfield;
	}

	.ant-input:disabled,
	.ant-select-disabled .ant-select-selector {
		background-color: #e5e7eb;
		color: #6b7280;
	}

	.ant-select-selector {
		display: flex;
		align-items: center;
	}

	.ant-picker {
		width: 100%;
		display: flex;
		align-items: center;
	}

	.ant-input:hover,
	.ant-select-selector:hover,
	.ant-picker:hover {
		border-color: #9ca3af;
	}

	.ant-input:focus,
	.ant-select-focused .ant-select-selector,
	.ant-picker-focused {
		border-color: #1e3a8a;
		box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
	}
`;

const FormGrid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
`;

// Per Order rate lines (Rate ID / Current Vendor Rate / New Vendor Rate).
const RateLinesTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 12px;

	th,
	td {
		padding: 8px 12px;
		text-align: left;
		border-bottom: 1px solid #e5e7eb;
		font-size: 13px;
	}
	th {
		color: #6b7280;
		font-weight: 500;
	}
	td .ant-input {
		max-width: 160px;
	}
`;

const SectionDivider = styled.div`
	border-top: 1px solid #e5e7eb;
	margin: 4px 0 20px;
`;

const ModalFooter = styled(Box)`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #d1d5db;
	color: #374151;
	background: white;
	font-weight: 500;
	border-radius: 6px;

	&:hover {
		border-color: #9ca3af !important;
		color: #111827 !important;
		background: white !important;
	}
`;

const ConfirmButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	color: white;
	font-weight: 500;
	border-radius: 6px;

	&:hover {
		background-color: #1e40af !important;
		border-color: #1e40af !important;
		color: white !important;
	}

	&:disabled {
		background-color: #9ca3af !important;
		border-color: #9ca3af !important;
		cursor: not-allowed;
	}
`;

/**
 * Entity-level vehicle replacement (used by vehicle management pages).
 * This is the original behaviour and is preserved as the default mode.
 */
function VehicleEntityReplaceModal({ visible, vehicleData, onConfirm, onClose }) {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [vehicleOptions, setVehicleOptions] = useState([]);

	// Fetch vehicles from same vendor, excluding the current vehicle
	useEffect(() => {
		if (visible && vehicleData?.vendor_id) {
			dispatch(
				searchVehiclesV2({
					filter_by: { vendor_id: vehicleData.vendor_id },
					pagination: { page: 1, per_page: 200 },
				})
			).then((response) => {
				if (response?.rows) {
					const options = response.rows
						.filter((v) => v.id !== vehicleData.id)
						.map((v) => ({
							value: v.id,
							label: v.vehicle_number,
						}));
					setVehicleOptions(options);
				}
			});
		}
	}, [visible, vehicleData, dispatch]);

	useEffect(() => {
		if (visible && vehicleData) {
			form.setFieldsValue({
				old_vehicle_number: vehicleData.vehicle_number,
				vendor_name: vehicleData.vendor_name,
				new_vehicle_id: undefined,
				reason: '',
				replacement_date: undefined,
			});
		}
	}, [visible, vehicleData, form]);

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	const handleConfirm = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			const formatDate = (d) => `${d.format('YYYY-MM-DD')}T00:00:00Z`;

			const replaceData = {
				old_vehicle_id: vehicleData.id,
				new_vehicle_id: values.new_vehicle_id,
				vendor_id: vehicleData.vendor_id,
				reason: values.reason,
				replacement_date: formatDate(values.replacement_date),
			};

			const success = await onConfirm(replaceData);
			if (success) {
				form.resetFields();
			}
		} catch (error) {
			console.error('Validation error:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<StyledModal
			title="Replace Vehicle"
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={800}
			destroyOnClose
		>
			<StyledForm form={form} layout="vertical">
				<FormGrid>
					<Form.Item name="old_vehicle_number" label="Vehicle Number">
						<Input disabled />
					</Form.Item>

					<Form.Item name="vendor_name" label="Vendor">
						<Input disabled />
					</Form.Item>

					<Form.Item
						name="new_vehicle_id"
						label="Replace with Vehicle"
						rules={[{ required: true, message: 'Please select a vehicle' }]}
					>
						<Select placeholder="Select vehicle" showSearch optionFilterProp="children">
							{vehicleOptions.map((v) => (
								<Option key={v.value} value={v.value}>
									{v.label}
								</Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item
						name="reason"
						label="Reason for Replacement"
						rules={[{ required: true, message: 'Please enter reason' }]}
					>
						<Input placeholder="Enter reason" />
					</Form.Item>

					<Form.Item
						name="replacement_date"
						label="Replacement Date"
						rules={[{ required: true, message: 'Please select replacement date' }]}
					>
						<DatePicker
							format="DD-MM-YYYY"
							placeholder="DD-MM-YYYY"
							style={{ width: '100%' }}
						/>
					</Form.Item>
				</FormGrid>

				<ModalFooter>
					<CancelButton onClick={handleCancel}>Cancel</CancelButton>
					<ConfirmButton onClick={handleConfirm} loading={loading}>
						Confirm
					</ConfirmButton>
				</ModalFooter>
			</StyledForm>
		</StyledModal>
	);
}

VehicleEntityReplaceModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	vehicleData: PropTypes.shape({
		id: PropTypes.number,
		vehicle_number: PropTypes.string,
		vendor_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_name: PropTypes.string,
	}),
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};
// eslint-disable-next-line sonarjs/cognitive-complexity
function TripReplaceVehicleModal({ visible, tripData, onConfirm, onClose }) {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [differentVendor, setDifferentVendor] = useState(false);
	// Same-vendor replacements keep the current driver by default; ticking this
	// unlocks the New Driver field so the driver can be changed too.
	const [changeDriver, setChangeDriver] = useState(false);
	const [tripCostOption, setTripCostOption] = useState('same');
	const [revisedCost, setRevisedCost] = useState(undefined);

	const [vendorOptions, setVendorOptions] = useState([]);
	const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
	const [vehicleOptions, setVehicleOptions] = useState([]);
	const [driverOptions, setDriverOptions] = useState([]);

	const currentVendorId = tripData?.vendor_id;
	const currentVehicleTypeId = tripData?.vehicle_type_id;
	const currentVehicleNumber = tripData?.vehicle_number;

	const normalizedRateBasis = String(tripData?.vendor_rate_basis || '')
		.toLowerCase()
		.replace(/\s+/g, '_');
	const isPerKm = normalizedRateBasis === 'per_km';
	const isPerOrder = normalizedRateBasis === 'per_order';
	const isFixedPerDay =
		String(tripData?.billing_type || '').trim() === BILLING_TYPE_FIXED_PER_DAY;
	const isPerOrderFixedPerDay = isPerOrder && isFixedPerDay;
	const orderRates = Array.isArray(tripData?.order_rates) ? tripData.order_rates : [];
	let currentRateLabel = 'Old Vendor Rate';
	let rateLabel = 'New Vendor Rate';
	if (isPerKm) {
		currentRateLabel = 'Current Charges per KM';
		rateLabel = 'New Charges per KM';
	} else if (isPerOrder) {
		currentRateLabel = 'Old Vendor Per Order Rate';
		rateLabel = 'New Vendor Per Order Rate';
	}
	const currentRateValue = isPerKm
		? tripData?.current_charges_per_km
		: tripData?.current_vendor_rate ?? tripData?.current_cost;
	const currentRate = Number(currentRateValue);
	const priceDiff =
		tripCostOption === 'updated' &&
		Number.isFinite(currentRate) &&
		Number.isFinite(Number(revisedCost))
			? Number(revisedCost) - currentRate
			: 0;
	useEffect(() => {
		if (visible) {
			setDifferentVendor(false);
			setChangeDriver(false);
			setTripCostOption('same');
			setRevisedCost(undefined);
			form.setFieldsValue({
				vendor_name: tripData?.vendor_name,
				vehicle_number: tripData?.vehicle_number,
				vehicle_type: tripData?.vehicle_type,
				driver_name: tripData?.driver_name,
				different_vendor: false,
				new_vendor_id: undefined,
				new_vehicle_type_id: undefined,
				new_vehicle_id: undefined,
				new_driver_id: undefined,
				previous_vehicle_end_km: undefined,
				new_vehicle_start_km: undefined,
				reason: '',
				trip_cost_option: 'same',
				current_cost:
					String(tripData?.vendor_rate_basis || '')
						.toLowerCase()
						.replace(/\s+/g, '_') === 'per_km'
						? tripData?.current_charges_per_km
						: tripData?.current_vendor_rate ?? tripData?.current_cost,
				revised_cost: undefined,
				new_fixed_rate_per_day: undefined,
			});
		}
	}, [visible, tripData, form]);
	useEffect(() => {
		if (!visible) return;

		dispatch(
			searchVendorV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((res) => {
			const options = (res?.rows || [])
				.filter((item) => String(item?.id) !== String(currentVendorId))
				.map((item) => ({
					value: item?.id,
					label: item?.vendor_name || '-',
				}));
			setVendorOptions(options);
		});

		dispatch(
			searchVehicleTypesV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((res) => {
			const options = (res?.rows || []).map((item) => ({
				value: item?.id,
				label: item?.vehicle_type_name || item?.name || '-',
			}));
			setVehicleTypeOptions(options);
		});
	}, [visible, dispatch, currentVendorId]);
	const loadVehiclesAndDrivers = React.useCallback(
		(vendorId, vehicleTypeId, excludeVehicleNumber = null) => {
			if (!vendorId || !vehicleTypeId) {
				setVehicleOptions([]);
				setDriverOptions([]);
				return;
			}

			dispatch(
				searchVehiclesV2({
					filter_by: {
						vendor_id: vendorId,
						vehicle_type_id: Number(vehicleTypeId),
						is_active: true,
					},
					pagination: { page: 1, per_page: 1000 },
				})
			).then((res) => {
				const options = (res?.rows || [])
					.filter(
						(v) => !excludeVehicleNumber || v.vehicle_number !== excludeVehicleNumber
					)
					.map((v) => ({
						value: v.id,
						label: v.vehicle_number || '-',
						item: v,
					}));
				setVehicleOptions(options);
			});

			dispatch(
				searchDriversV2({
					filter_by: {
						vendor_id: vendorId,
						vehicle_type_ids: [Number(vehicleTypeId)],
						is_active: true,
					},
					offset: 0,
					limit: 1000,
				})
			).then((res) => {
				const options = (res?.rows || []).map((d) => ({
					value: d.id,
					label: d.driver_name || d.name || '-',
					item: d,
				}));
				setDriverOptions(options);
			});
		},
		[dispatch]
	);
	useEffect(() => {
		if (visible && !differentVendor) {
			loadVehiclesAndDrivers(currentVendorId, currentVehicleTypeId, currentVehicleNumber);
		}
	}, [
		visible,
		differentVendor,
		currentVendorId,
		currentVehicleTypeId,
		currentVehicleNumber,
		loadVehiclesAndDrivers,
	]);

	const handleDifferentVendorChange = (e) => {
		const { checked } = e.target;
		setDifferentVendor(checked);
		// Different vendor always needs a new driver from that vendor, so the
		// same-vendor "change driver" toggle is reset when switching modes.
		setChangeDriver(false);
		// Clear dependent selections when toggling
		form.setFieldsValue({
			new_vendor_id: undefined,
			new_vehicle_type_id: undefined,
			new_vehicle_id: undefined,
			new_driver_id: undefined,
		});
		setVehicleOptions([]);
		setDriverOptions([]);
		if (!checked) {
			loadVehiclesAndDrivers(currentVendorId, currentVehicleTypeId, currentVehicleNumber);
		}
	};

	const handleNewVendorChange = (vendorId) => {
		form.setFieldsValue({ new_vehicle_id: undefined, new_driver_id: undefined });
		const vehicleTypeId = form.getFieldValue('new_vehicle_type_id');
		loadVehiclesAndDrivers(vendorId, vehicleTypeId);
	};

	const handleNewVehicleTypeChange = (vehicleTypeId) => {
		form.setFieldsValue({ new_vehicle_id: undefined, new_driver_id: undefined });
		const vendorId = form.getFieldValue('new_vendor_id');
		loadVehiclesAndDrivers(vendorId, vehicleTypeId);
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	const handleConfirm = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			// Driver changes only when a different vendor is chosen (needs a new
			// vendor's driver) or the same-vendor "change driver" box is ticked.
			// Otherwise omit new_driver_id so the backend keeps the current driver.
			const isChangingDriver = differentVendor || changeDriver;

			const selectedVehicle = vehicleOptions.find((v) => v.value === values.new_vehicle_id);
			const selectedDriver = driverOptions.find((d) => d.value === values.new_driver_id);
			const selectedVendor = vendorOptions.find((v) => v.value === values.new_vendor_id);
			const selectedType = vehicleTypeOptions.find(
				(t) => t.value === values.new_vehicle_type_id
			);

			// Map the form to the trip_replace_vehicle API contract.
			const isUpdatedCost = differentVendor && !isPerOrder && tripCostOption === 'updated';
			const revisedRate = isUpdatedCost ? Number(values.revised_cost) : undefined;
			// Fixed Per Day + Per Order swaps let the operator keep the current daily
			// rate ('same') or enter a new one ('updated') via the radio. Only the
			// 'updated' case freezes the per-line rates and sends a new daily rate.
			const isFpdPerOrderSwap = differentVendor && isPerOrderFixedPerDay;
			const isFpdRateUpdated = isFpdPerOrderSwap && tripCostOption === 'updated';
			const frozenOrderRates = isFpdRateUpdated
				? orderRates.filter((r) => r.vendor_rate != null)
				: [];
			if (isFpdRateUpdated && frozenOrderRates.length === 0) {
				message.error(
					'No per-order rates found for this trip; cannot record a different-vendor replacement.'
				);
				return;
			}
			// Plain Per Order always keeps the same cost; every other billing type
			// (Fixed Per Day + Per Order included, via its own Same/Updated radio)
			// honors the selected option.
			let tripCostModeToSend = tripCostOption;
			if (isPerOrder && !isPerOrderFixedPerDay) {
				tripCostModeToSend = 'same';
			}

			const response = await dispatch(
				replaceTripVehicleV2({
					trip_id: tripData?.trip_id,
					is_different_vendor: differentVendor,
					new_vehicle_id: values.new_vehicle_id,
					new_driver_id: isChangingDriver ? values.new_driver_id : undefined,
					old_vehicle_odo_at_swap: Number(values.previous_vehicle_end_km),
					new_vehicle_start_odo: Number(values.new_vehicle_start_km),
					reason: values.reason,
					...(differentVendor
						? {
								new_vendor_id: values.new_vendor_id,
								new_vehicle_type_id: values.new_vehicle_type_id,
								// Per Order rates never change on replacement → "same",
								// EXCEPT Fixed Per Day + Per Order, which honors its
								// Same/Updated radio.
								trip_cost_mode: tripCostModeToSend,
								// FLAT → new_rate, PER KM → new_per_km (Per Order sends neither).
								new_rate: !isPerKm && !isPerOrder ? revisedRate : undefined,
								new_per_km: isPerKm ? revisedRate : undefined,
								// Fixed Per Day + Per Order, "Updated" only: freeze the current
								// per-line vendor rates and send the operator's new daily rate.
								...(isFpdRateUpdated
									? {
											order_rate_override: frozenOrderRates.map((r) => ({
												rate_id: r.rate_id,
												new_rate: Number(r.vendor_rate),
											})),
											new_fixed_rate_per_day: Number(
												values.new_fixed_rate_per_day
											),
									  }
									: {}),
						  }
						: {}),
				})
			);

			if (response?.status !== 'success') {
				return;
			}

			const payload = {
				trip_id: tripData?.trip_id,
				replacement_id: response?.data?.replacement_id,
				old_vehicle_number: tripData?.vehicle_number,
				different_vendor: response?.data?.is_different_vendor ?? differentVendor,
				new_vehicle_id: response?.data?.new_vehicle_id ?? values.new_vehicle_id,
				new_vehicle_number: selectedVehicle?.label,
				new_driver_id: isChangingDriver
					? response?.data?.new_driver_id ?? values.new_driver_id
					: undefined,
				new_driver_name: isChangingDriver ? selectedDriver?.label : undefined,
				new_driver_number: isChangingDriver
					? selectedDriver?.item?.mobile_number ||
					  selectedDriver?.item?.driver_number ||
					  ''
					: undefined,
				old_vehicle_odo_at_swap: Number(values.previous_vehicle_end_km),
				new_vehicle_start_odo: Number(values.new_vehicle_start_km),
				reason: values.reason,
				old_rate: response?.data?.old_rate,
				new_rate: response?.data?.new_rate,
				deduction_amount: response?.data?.deduction_amount,
				deduction_status: response?.data?.deduction_status,
				...(differentVendor
					? {
							new_vendor_id: values.new_vendor_id,
							new_vendor_name: selectedVendor?.label,
							new_vehicle_type_id: values.new_vehicle_type_id,
							new_vehicle_type_name: selectedType?.label,
							trip_cost_mode: tripCostOption,
					  }
					: {}),
			};

			const success = await onConfirm(payload);
			if (success) {
				form.resetFields();
			}
		} catch (error) {
			console.error('Validation error:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<StyledModal
			title="Replace Vehicle"
			open={visible}
			onCancel={handleCancel}
			footer={null}
			width={800}
			destroyOnClose
		>
			<StyledForm form={form} layout="vertical">
				<FormGrid>
					<Form.Item name="vendor_name" label="Vendor">
						<Input disabled />
					</Form.Item>
					<Form.Item name="vehicle_number" label="Vehicle Number">
						<Input disabled />
					</Form.Item>
					<Form.Item name="vehicle_type" label="Vehicle Type">
						<Input disabled />
					</Form.Item>
					<Form.Item name="driver_name" label="Driver Name">
						<Input disabled />
					</Form.Item>
				</FormGrid>

				<Form.Item
					name="different_vendor"
					valuePropName="checked"
					style={{ marginBottom: 20 }}
				>
					<Checkbox onChange={handleDifferentVendorChange}>
						Select different vendor
					</Checkbox>
				</Form.Item>

				{differentVendor && (
					<FormGrid>
						<Form.Item
							name="new_vendor_id"
							label="Select New Vendor"
							rules={[{ required: true, message: 'Please select a vendor' }]}
						>
							<Select
								placeholder="Select New Vendor"
								showSearch
								optionFilterProp="children"
								onChange={handleNewVendorChange}
							>
								{vendorOptions.map((v) => (
									<Option key={v.value} value={v.value}>
										{v.label}
									</Option>
								))}
							</Select>
						</Form.Item>
						<Form.Item
							name="new_vehicle_type_id"
							label="Select Vehicle Type"
							rules={[{ required: true, message: 'Please select a vehicle type' }]}
						>
							<Select
								placeholder="Select new Vehicle Type"
								showSearch
								optionFilterProp="children"
								onChange={handleNewVehicleTypeChange}
							>
								{vehicleTypeOptions.map((t) => (
									<Option key={t.value} value={t.value}>
										{t.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</FormGrid>
				)}

				<FormGrid>
					<Form.Item
						name="new_vehicle_id"
						label="Replacement Vehicle"
						rules={[{ required: true, message: 'Please select a vehicle' }]}
					>
						<Select placeholder="Select Vehicle" showSearch optionFilterProp="children">
							{vehicleOptions.map((v) => (
								<Option key={v.value} value={v.value}>
									{v.label}
								</Option>
							))}
						</Select>
					</Form.Item>
					<div style={{ position: 'relative' }}>
						{!differentVendor && (
							<Checkbox
								style={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
								checked={changeDriver}
								onChange={(e) => {
									const { checked } = e.target;
									setChangeDriver(checked);
									if (!checked) {
										form.setFieldsValue({ new_driver_id: undefined });
									}
								}}
							/>
						)}
						<Form.Item
							name="new_driver_id"
							label="New Driver Name"
							rules={
								differentVendor || changeDriver
									? [{ required: true, message: 'Please select a driver' }]
									: []
							}
						>
							<Select
								placeholder="Select Driver"
								showSearch
								optionFilterProp="children"
								disabled={!(differentVendor || changeDriver)}
							>
								{driverOptions.map((d) => (
									<Option key={d.value} value={d.value}>
										{d.label}
									</Option>
								))}
							</Select>
						</Form.Item>
					</div>
				</FormGrid>

				<FormGrid>
					<Form.Item
						name="previous_vehicle_end_km"
						label="Previous Vehicle End KM"
						rules={[
							{ required: true, message: 'Please enter previous vehicle end KM' },
						]}
					>
						<Input type="number" placeholder="Enter previous vehicle end KM" />
					</Form.Item>
					<Form.Item
						name="new_vehicle_start_km"
						label="New Vehicle Start KM"
						rules={[{ required: true, message: 'Please enter new vehicle start KM' }]}
					>
						<Input type="number" placeholder="Enter new vehicle start KM" />
					</Form.Item>
				</FormGrid>

				<Form.Item
					name="reason"
					label="Reason for Replacement"
					rules={[{ required: true, message: 'Please enter reason' }]}
				>
					<Input placeholder="Enter Reason" />
				</Form.Item>

				{differentVendor &&
					(isPerOrder ? (
						<>
							<Form.Item label="Trip Cost :" style={{ marginBottom: 8 }}>
								<div style={{ color: '#475569', fontSize: 13 }}>
									Per Order rates remain unchanged on replacement.
								</div>
							</Form.Item>
							<RateLinesTable>
								<thead>
									<tr>
										<th>Rate ID</th>
										<th>Vendor Per Order Rate</th>
									</tr>
								</thead>
								<tbody>
									{orderRates.map((r) => (
										<tr key={r.rate_id}>
											<td>{r.rate_id}</td>
											<td>{r.vendor_rate}</td>
										</tr>
									))}
								</tbody>
							</RateLinesTable>
							{isPerOrderFixedPerDay && (
								<>
									<Form.Item
										label="Fixed Rate Per Day :"
										style={{ marginBottom: 12, marginTop: 16 }}
									>
										<Radio.Group
											value={tripCostOption}
											onChange={(e) => {
												setTripCostOption(e.target.value);
												form.setFieldsValue({
													trip_cost_option: e.target.value,
												});
												if (e.target.value === 'same') {
													form.setFieldsValue({
														new_fixed_rate_per_day: undefined,
													});
												}
											}}
										>
											<Radio value="same">Same</Radio>
											<Radio value="updated">Updated</Radio>
										</Radio.Group>
									</Form.Item>
									{tripCostOption === 'updated' && (
										<FormGrid>
											<Form.Item
												name="new_fixed_rate_per_day"
												label="New Fixed Rate Per Day"
												rules={[
													{
														required: true,
														message:
															'Please enter the new fixed rate per day',
													},
												]}
											>
												<Input
													type="number"
													placeholder="Enter new fixed rate per day"
												/>
											</Form.Item>
										</FormGrid>
									)}
								</>
							)}
						</>
					) : (
						<>
							<Form.Item label="Trip Cost :" style={{ marginBottom: 12 }}>
								<Radio.Group
									value={tripCostOption}
									onChange={(e) => {
										setTripCostOption(e.target.value);
										form.setFieldsValue({
											trip_cost_option: e.target.value,
										});
										if (e.target.value === 'same') {
											setRevisedCost(undefined);
											form.setFieldsValue({ revised_cost: undefined });
										}
									}}
								>
									<Radio value="same">Same</Radio>
									<Radio value="updated">Updated</Radio>
								</Radio.Group>
							</Form.Item>
							<FormGrid>
								<Form.Item name="current_cost" label={currentRateLabel}>
									<Input disabled />
								</Form.Item>
								{tripCostOption === 'updated' && (
									<Form.Item
										name="revised_cost"
										label={rateLabel}
										rules={[
											{
												required: true,
												message: 'Please enter revised price',
											},
										]}
									>
										<Input
											type="number"
											placeholder="Enter revised price"
											onChange={(e) => setRevisedCost(e.target.value)}
										/>
									</Form.Item>
								)}
							</FormGrid>
							{priceDiff > 0 && (
								<Alert
									type="warning"
									style={{ marginBottom: 20, textAlign: 'center' }}
									message={
										<span
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												gap: 8,
												color: '#9E3900',
												fontWeight: 500,
											}}
										>
											<WarningFilled style={{ color: '#9E3900' }} />
											<span>
												Note : The {rateLabel} exceeds the{' '}
												{currentRateLabel} by{' '}
												<span style={{ fontWeight: 700, fontSize: 16 }}>
													₹{priceDiff.toLocaleString('en-IN')}
												</span>
											</span>
										</span>
									}
								/>
							)}
						</>
					))}

				<SectionDivider />

				<ModalFooter>
					<CancelButton onClick={handleCancel}>Cancel</CancelButton>
					<ConfirmButton onClick={handleConfirm} loading={loading}>
						Confirm
					</ConfirmButton>
				</ModalFooter>
			</StyledForm>
		</StyledModal>
	);
}

TripReplaceVehicleModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	tripData: PropTypes.shape({
		trip_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_name: PropTypes.string,
		vehicle_number: PropTypes.string,
		vehicle_type: PropTypes.string,
		vehicle_type_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		driver_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		driver_name: PropTypes.string,
		current_cost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		current_vendor_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		current_charges_per_km: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
		vendor_rate_basis: PropTypes.string,
		billing_type: PropTypes.string,
		billing_subtype: PropTypes.string,
		order_rates: PropTypes.arrayOf(
			PropTypes.shape({
				rate_id: PropTypes.string,
				client_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
				vendor_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
			})
		),
	}),
	onConfirm: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

function ReplaceVehicleModal({ mode, ...props }) {
	if (mode === 'trip') {
		return <TripReplaceVehicleModal {...props} />;
	}
	return <VehicleEntityReplaceModal {...props} />;
}

ReplaceVehicleModal.propTypes = {
	mode: PropTypes.oneOf(['vehicle', 'trip']),
};

ReplaceVehicleModal.defaultProps = {
	mode: 'vehicle',
};

export default ReplaceVehicleModal;
